;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:propstrata-app":true});}catch(e){}}();import { ApiRequestError } from "@/lib/api/api-request-error";
import { resolveRequestUrl } from "@/lib/api/resolve-request-url";
import { emitter } from "@/lib/emitter";
import { getApiErrorMessage } from "@/lib/utils/api-route-response";
import {
	createVisualizationTraceId,
	parseServerTimingHeader,
	VISUALIZATION_TRACE_HEADER,
	type VisualizationRequestPerformance,
} from "@/lib/utils/visualization-trace";
import { getQueryClient } from "@/queries/config/query-client";

export interface RequestOptions extends RequestInit {
	skipAuth?: boolean;
	token?: string;
}

const queryClient = getQueryClient();

/** POST bodies that share one in-flight fetch (React Query remounts, duplicate observers). */
const inFlightPostByUrlAndBody = new Map<string, Promise<unknown>>();

function dedupeInFlightPost<T>(
	dedupeKey: string,
	run: () => Promise<T>,
): Promise<T> {
	const existing = inFlightPostByUrlAndBody.get(dedupeKey);
	if (existing) {
		return existing as Promise<T>;
	}

	const promise = run().finally(() => {
		inFlightPostByUrlAndBody.delete(dedupeKey);
	});

	inFlightPostByUrlAndBody.set(dedupeKey, promise);
	return promise as Promise<T>;
}

function shouldDedupeVisualizationPost(
	url: string,
	options: RequestInit,
): boolean {
	if (options.method?.toUpperCase() !== "POST") {
		return false;
	}
	if (typeof options.body !== "string") {
		return false;
	}
	return (
		url === "/api/visualizations/data" ||
		url === "/api/visualizations/drill-down" ||
		url === "/api/visualizations/leasing-card-summaries"
	);
}

function shouldDedupeOAuthCallbackPost(
	url: string,
	options: RequestInit,
): boolean {
	if (options.method?.toUpperCase() !== "POST") {
		return false;
	}
	if (typeof options.body !== "string") {
		return false;
	}

	return /\/data-provider\/(?:zoom|podium|goto|quickbooks)\/?$/.test(url);
}

function shouldDedupeNotificationSummaryGet(
	url: string,
	options: RequestInit,
): boolean {
	const method = (options.method ?? "GET").toUpperCase();
	if (method !== "GET") {
		return false;
	}

	return url === "/api/notifications?summary=unread";
}

function getInFlightDedupeKey(
	url: string,
	options: RequestInit,
): string | null {
	if (shouldDedupeNotificationSummaryGet(url, options)) {
		return url;
	}

	if (
		shouldDedupeVisualizationPost(url, options) ||
		shouldDedupeOAuthCallbackPost(url, options)
	) {
		return `${url}\u0000${options.body as string}`;
	}

	return null;
}

function isVisualizationPost(url: string, options: RequestInit): boolean {
	return shouldDedupeVisualizationPost(url, options);
}

export async function queryFetcher<T>(
	url: string,
	{ token: tokenFromOptions, ...options }: RequestOptions,
): Promise<T> {
	const execute = async (): Promise<T> => {
		const abortController = new AbortController();
		const headers = new Headers(options.headers || {});
		const token = tokenFromOptions || "";
		let visualizationTraceId: string | undefined;

		// Add content-type if not already set
		if (!headers.has("Content-Type") && options.body) {
			headers.set("Content-Type", "application/json");
		}

		// Add authorization header if user is authenticated
		if (!options.skipAuth && token) {
			headers.set("Authorization", `Bearer ${token}`);
		}

		const requestUrl = resolveRequestUrl(url);

		if (isVisualizationPost(url, options)) {
			visualizationTraceId =
				headers.get(VISUALIZATION_TRACE_HEADER) || createVisualizationTraceId();
			headers.set(VISUALIZATION_TRACE_HEADER, visualizationTraceId);
		}

		const response = await fetch(requestUrl, {
			...options,
			credentials: "include",
			headers,
			signal: abortController.signal,
		});

		if (!response.ok) {
			const errorData = await response.json().catch(() => ({}));

			// Emit 401 statuses
			if (response.status === 401) {
				abortController.abort("user.unauthorized");
				// Do not await: cancellation rejects in-flight queryFns with CancelledError,
				// which can reject this same call stack (e.g. during sign-in prefetch).
				void queryClient.cancelQueries().catch(() => {});
				emitter.emit("user.unauthorized");
			}

			throw new ApiRequestError({
				message: getApiErrorMessage(
					errorData,
					`Request failed with status ${response.status}`,
				),
				status: response.status,
			});
		}

		const responseJson = (await response.json()) as T;

		if (
			isVisualizationPost(url, options) &&
			responseJson &&
			typeof responseJson === "object"
		) {
			const traceIdFromResponse =
				response.headers.get(VISUALIZATION_TRACE_HEADER) ||
				visualizationTraceId;
			const requestPerformance: VisualizationRequestPerformance = {
				serverTimingsMs: parseServerTimingHeader(
					response.headers.get("server-timing"),
				),
				traceId: traceIdFromResponse ?? undefined,
			};

			(
				responseJson as T & {
					__requestPerformance?: VisualizationRequestPerformance;
				}
			).__requestPerformance = requestPerformance;
		}

		return responseJson;
	};

	const inFlightDedupeKey = getInFlightDedupeKey(url, options);
	if (inFlightDedupeKey) {
		return dedupeInFlightPost(inFlightDedupeKey, execute);
	}

	return execute();
}
